import { ReactNode } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { cn } from '@/utils/cn'
import { NativeSelect } from "@/components/ui/native-select"

interface PaginationProps {
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
  showSizeChanger?: boolean
  pageSize?: number
  pageSizeOptions?: number[]
  onPageSizeChange?: (size: number) => void
  totalItems?: number
  className?: string
  showQuickJumper?: boolean
}

interface PaginationContentProps {
  children: ReactNode
  className?: string
}

interface PaginationItemProps {
  children: ReactNode
  isActive?: boolean
  disabled?: boolean
  onClick?: () => void
  className?: string
}

interface PaginationLinkProps extends PaginationItemProps {
  href?: string
}

interface PaginationPreviousProps {
  onClick?: () => void
  disabled?: boolean
  className?: string
}

interface PaginationNextProps {
  onClick?: () => void
  disabled?: boolean
  className?: string
}

interface PaginationEllipsisProps {
  className?: string
}

export function Pagination({
  currentPage,
  totalPages,
  onPageChange,
  showSizeChanger = false,
  pageSize = 20,
  pageSizeOptions = [10, 20, 50, 100],
  onPageSizeChange,
  totalItems,
  className,
  showQuickJumper = false
}: PaginationProps) {
  const getVisiblePages = (): (number | string)[] => {
    const delta = 2
    const range: number[] = []
    const rangeWithDots: (number | string)[] = []

    for (let i = Math.max(2, currentPage - delta); 
         i <= Math.min(totalPages - 1, currentPage + delta); 
         i++) {
      range.push(i)
    }

    if (currentPage - delta > 2) {
      rangeWithDots.push(1, '...')
    } else {
      rangeWithDots.push(1)
    }

    rangeWithDots.push(...range)

    if (currentPage + delta < totalPages - 1) {
      rangeWithDots.push('...', totalPages)
    } else if (totalPages > 1) {
      rangeWithDots.push(totalPages)
    }

    return rangeWithDots
  }

  const handleQuickJump = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      const value = parseInt((e.target as HTMLInputElement).value)
      if (value >= 1 && value <= totalPages) {
        onPageChange(value)
      }
      (e.target as HTMLInputElement).value = ''
    }
  }

  if (totalPages <= 1) return null

  return (
    <div className={cn('flex flex-wrap items-center justify-between gap-3', className)}>
      <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
        {totalItems && (
          <span>
            Mostrando {((currentPage - 1) * pageSize) + 1} até {Math.min(currentPage * pageSize, totalItems)} de {totalItems} resultados
          </span>
        )}
        
        {showSizeChanger && onPageSizeChange && (
          <div className="flex items-center space-x-2">
            <span>Itens por página:</span>
            <NativeSelect
              value={pageSize}
              onChange={(e) => onPageSizeChange(parseInt(e.target.value))}
              className="rounded-md border border-input px-2 py-1 text-sm"
            >
              {pageSizeOptions.map(option => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </NativeSelect>
          </div>
        )}
      </div>

      <div className="flex min-w-0 flex-wrap items-center gap-4">
        {showQuickJumper && (
          <div className="flex items-center space-x-2 text-sm">
            <span>Ir para:</span>
            <input
              type="number"
              min={1}
              max={totalPages}
              placeholder={String(currentPage)}
              onKeyDown={handleQuickJump}
              className="w-16 rounded-md border border-input px-2 py-1 text-center text-sm"
            />
          </div>
        )}

        <PaginationContent>
          <PaginationPrevious
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage <= 1}
          />

          {getVisiblePages().map((page, index) => (
            <PaginationItem
              key={index}
              isActive={page === currentPage}
              onClick={typeof page === 'number' ? () => onPageChange(page) : undefined}
              disabled={typeof page !== 'number'}
            >
              {page}
            </PaginationItem>
          ))}

          <PaginationNext
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage >= totalPages}
          />
        </PaginationContent>
      </div>
    </div>
  )
}

export function PaginationContent({ children, className }: PaginationContentProps) {
  return (
    <nav className={cn('mx-auto flex w-full justify-center', className)} role="navigation" aria-label="pagination">
      <ul className="flex flex-row flex-wrap items-center justify-center gap-1">
        {children}
      </ul>
    </nav>
  )
}

export function PaginationItem({
  children,
  isActive = false,
  disabled = false,
  onClick,
  className
}: PaginationItemProps) {
  return (
    <li>
      <button
        onClick={onClick}
        disabled={disabled}
        className={cn(
          'flex h-10 w-10 items-center justify-center rounded-md border text-sm font-medium transition-colors',
          'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
          'disabled:pointer-events-none disabled:opacity-50',
          isActive
            ? 'border-primary bg-primary text-primary-foreground hover:bg-primary/90'
            : 'border-input bg-background text-muted-foreground hover:bg-muted',
          className
        )}
      >
        {children}
      </button>
    </li>
  )
}

export function PaginationLink({ children, href, ...props }: PaginationLinkProps) {
  if (href) {
    return (
      <li>
        <a href={href} className={cn(
          'flex h-10 w-10 items-center justify-center rounded-md border text-sm font-medium transition-colors',
          'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
          props.isActive
            ? 'border-primary bg-primary text-primary-foreground hover:bg-primary/90'
            : 'border-input bg-background text-muted-foreground hover:bg-muted',
          props.className
        )}>
          {children}
        </a>
      </li>
    )
  }

  return <PaginationItem {...props}>{children}</PaginationItem>
}

export function PaginationPrevious({ 
  onClick, 
  disabled = false, 
  className 
}: PaginationPreviousProps) {
  return (
    <li>
      <button
        onClick={onClick}
        disabled={disabled}
        className={cn(
          'flex h-10 items-center justify-center rounded-md border px-4 text-sm font-medium transition-colors',
          'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
          'disabled:pointer-events-none disabled:opacity-50',
          'border-input bg-background text-muted-foreground hover:bg-muted',
          className
        )}
      >
        <ChevronLeft className="h-4 w-4 mr-1" />
        Anterior
      </button>
    </li>
  )
}

export function PaginationNext({ 
  onClick, 
  disabled = false, 
  className 
}: PaginationNextProps) {
  return (
    <li>
      <button
        onClick={onClick}
        disabled={disabled}
        className={cn(
          'flex h-10 items-center justify-center rounded-md border px-4 text-sm font-medium transition-colors',
          'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
          'disabled:pointer-events-none disabled:opacity-50',
          'border-input bg-background text-muted-foreground hover:bg-muted',
          className
        )}
      >
        Próxima
        <ChevronRight className="h-4 w-4 ml-1" />
      </button>
    </li>
  )
}

export function PaginationEllipsis({ className }: PaginationEllipsisProps) {
  return (
    <li>
      <span className={cn(
        'flex h-10 w-10 items-center justify-center text-sm',
        className
      )}>
        ...
      </span>
    </li>
  )
}